import { Link } from "wouter";
import { ArrowRight, Zap, Users, Award, Wrench, Factory, Shield, TrendingUp, Rocket } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";
import RelatedArticles from "@/components/RelatedArticles";

/*
 * OmOss.tsx — About page for ASPAB
 * Design: White/light theme, blue (#2563EB) accents
 * Typography: Space Grotesk headings, Inter body
 * Enhanced timeline with icons, images, and quotes
 */

const timeline = [
  {
    year: "1993",
    title: "ASPAB grundas i Eskilstuna",
    desc: "Företaget grundas med fokus på metallbearbetning och laserskärning. Från start etableras en kultur av precision och kvalitet som kommer att definiera verksamheten i årtionden framåt.",
    icon: Factory,
    accent: false,
  },
  {
    year: "2000-tal",
    title: "Etablerad leverantör i regionen",
    desc: "ASPAB växer stadigt och bygger upp ett rykte som en pålitlig partner inom laserskärning. Kundbasen breddas till att inkludera fordonsindustri, tillverkningsindustri och konstruktionsbranschen.",
    icon: TrendingUp,
    accent: false,
  },
  {
    year: "Aug 2020",
    title: "Nytt ägarskap — en ny era börjar",
    desc: "Robin Fischer och hans team tar över verksamheten. Trots en intensiv övergångsperiod med hög arbetsbelastning byggde teamet snabbt upp kompetens och etablerade nya rutiner.",
    icon: Rocket,
    accent: true,
  },
  {
    year: "2021",
    title: "Stabilisering och kapacitetsuppbyggnad",
    desc: "Efter den kaotiska starten börjar verksamheten stabiliseras. Teamet bygger upp kompetens, processer och kundrelationer. Fokus ligger på att leverera snabbt och med hög kvalitet — grunden läggs för den snabbhet som idag är ASPAB:s främsta konkurrensfördel.",
    icon: Wrench,
    accent: false,
  },
  {
    year: "2022",
    title: "Partnerskap med Volvo",
    desc: "ASPAB blir Volvos go-to-leverantör för alla prototyper och kundanpassningar. Partnerskapet bevisar ASPAB:s förmåga att leverera både precision och volym, från enstaka prototyper till stora serier.",
    icon: Users,
    accent: false,
  },
  {
    year: "2023–24",
    title: "Investering i modern maskinpark",
    desc: "Intensiv investeringsperiod. Tre moderna CO2 Lasrar (6 kW och 5 kW Trumpf, 5 kW Amada), tre kantpressar (310, 160 och 100 tons), Finn Power stansmaskin och en stor Hedelius borr/fräs installeras. ASPAB har nu en av regionens mest moderna maskinparker.",
    icon: Zap,
    accent: true,
    quote: "Vi har jävligt bra toleranser och vi är måna om att skärssnittet ska vara bra och passformen optimerad.",
  },
  {
    year: "2025",
    title: "Rörlaser och ISO-certifiering",
    desc: "En 5 kW Trumpf rörlaser installeras — en investering som öppnar helt nya möjligheter inom konstruktionsstål, fyrkantsprofiler och rör. Samtidigt påbörjas certifiering enligt ISO 9001, ISO 14001 och ISO 45001, vilket möjliggör leveranser till försvarsindustrin och andra krävande sektorer.",
    icon: Shield,
    accent: true,
    quote: "Volvo väntar på vår rörlaser för att kunna köpa ännu fler tjänster av oss.",
  },
  {
    year: "2025",
    title: "Lissmac gradningsmaskin",
    desc: "ASPAB investerar i en Lissmac SBM-L 1500 G1S2 gradningsmaskin som bearbetar båda sidor av plåten samtidigt. Investeringen eliminerar vassa kanter på laserskurna detaljer och ger en jämnare ytfinish, vilket höjer kvaliteten ytterligare för krävande kunder.",
    icon: Zap,
    accent: false,
  },
  {
    year: "Framtid",
    title: "Expansion och innovation",
    desc: "Planer på vattenskärning, fortsatt automation med Sigma Nest-programvara, möjlig online-prissättning och expansion inom försvarsindustrin och jordbruksmaskiner. ASPAB siktar på att bli den självklara partnern för all metallbearbetning i Mälardalen.",
    icon: Award,
    accent: false,
  },
];

export default function OmOss() {
  return (
    <Layout>
      <SEOHead
        title="Om ASPAB - Metallbearbetning sedan 1993 i Eskilstuna"
        description="ASPAB har levererat laserskärning och metallbearbetning sedan 1993. Läs om vår historia, vårt team och vad som driver oss framåt."
        canonical="https://aspab.com/om-oss"
        path="/om-oss"
        breadcrumbs={[{ name: "Om oss", href: "/om-oss" }]}
              ogImage={IMAGES.ogOmOss}
      />

      <PageHero
        title="Om ASPAB"
        subtitle="Metallbearbetning med precision och passion sedan 1993. Vi bygger långsiktiga partnerskap genom kvalitet, snabbhet och problemlösning."
        breadcrumbs={[{ name: "Om oss", href: "/om-oss" }]}
      />

      {/* ASPAB - Ledande section from old site */}
      <section className="bg-white py-16 md:py-20 border-b border-gray-100">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 text-center">
                ASPAB – Ledande inom laserskärning
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed text-center">
                Teknisk kompetens och erfarenhet
              </p>
              <p className="text-gray-500 mt-4 leading-relaxed text-center">
                ASPAB är ett svenskt industriföretag med spetskompetens inom laserskärning, 
                kantpressning och svetsning. Med marknadens modernaste utrustning och ett 
                erfaret team levererar vi precision, kvalitet och snabbhet till industrier 
                som kräver det bästa.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Story */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-start">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Vår historia</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Från grundande till marknadsledare
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  ASPAB grundades 1993 i Eskilstuna och har sedan dess vuxit till en av regionens 
                  mest betrodda leverantörer av laserskärning och metallbearbetning.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  I augusti 2020 tog Robin Fischer och hans team över verksamheten. Den första tiden 
                  var intensiv, med dubbelt så mycket jobb och en brant inlärningskurva. Men teamet 
                  kämpade sig igenom och byggde upp en organisation med kapacitet att leverera snabbt 
                  och med hög kvalitet.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Idag har ASPAB folk och kapacitet både digitalt och ute på verkstadsgolvet. Vi kan 
                  hjälpa kunder snabbt, med skurna bitar samma dag eller dagen efter för enkla projekt 
                  och komplexa lösningar inom två dagar.
                </p>

                <blockquote className="mt-8 pl-5 border-l-2 border-[#2563EB]/50">
                  <p className="text-gray-500 italic leading-relaxed">
                    "Nu kan jag nog ta mig för bröstet och säga att vi faktiskt är snabba. Vi har 
                    folk och kapacitet och vi kan hjälpa kunder jäkligt fort."
                  </p>
                  <cite className="text-[#2563EB] text-sm mt-2 block not-italic">Robin Fischer, ASPAB</cite>
                </blockquote>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.workshop4}
                alt="ASPAB verkstad och maskinpark i Eskilstuna"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Enhanced Timeline */}
      <section className="bg-gray-50 py-20 md:py-28 overflow-hidden">
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Milstolpar</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Vår resa genom åren
              </h2>
              <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
                Från grundandet 1993 till dagens moderna verksamhet — varje steg har format 
                den leverantör vi är idag.
              </p>
            </div>
          </AnimatedSection>

          <div className="max-w-4xl mx-auto relative">
            {/* Vertical line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gray-200 md:-translate-x-px" />

            {timeline.map((item, i) => {
              const Icon = item.icon;
              const isLeft = i % 2 === 0;

              return (
                <AnimatedSection key={item.year} delay={i * 100}>
                  <div className={`relative flex items-start mb-12 last:mb-0 ${
                    isLeft ? "md:flex-row" : "md:flex-row-reverse"
                  }`}>
                    {/* Timeline dot / icon */}
                    <div className="absolute left-6 md:left-1/2 -translate-x-1/2 z-10">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center border-4 border-gray-50 shadow-sm ${
                        item.accent 
                          ? "bg-[#2563EB] text-white" 
                          : "bg-white text-[#2563EB] border-gray-200"
                      }`}>
                        <Icon className="w-5 h-5" />
                      </div>
                    </div>

                    {/* Content card */}
                    <div className={`ml-16 md:ml-0 md:w-[calc(50%-2.5rem)] ${
                      isLeft ? "md:pr-0 md:mr-auto" : "md:pl-0 md:ml-auto"
                    }`}>
                      <div className={`bg-white rounded-xl p-6 shadow-sm border transition-all duration-300 hover:shadow-md ${
                        item.accent 
                          ? "border-[#2563EB]/20 hover:border-[#2563EB]/40" 
                          : "border-gray-200 hover:border-gray-300"
                      }`}>
                        {/* Year badge */}
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold tracking-wide mb-3 ${
                          item.accent 
                            ? "bg-[#2563EB]/10 text-[#2563EB]" 
                            : "bg-gray-100 text-gray-600"
                        }`}>
                          {item.year}
                        </div>

                        <h3 className="font-heading text-lg font-bold text-gray-900">
                          {item.title}
                        </h3>
                        <p className="text-gray-500 text-sm mt-2 leading-relaxed">
                          {item.desc}
                        </p>

                        {/* Quote if available */}
                        {item.quote && (
                          <blockquote className="mt-4 pl-4 border-l-2 border-[#2563EB]/30">
                            <p className="text-gray-500 text-sm italic leading-relaxed">
                              "{item.quote}"
                            </p>
                            <cite className="text-[#2563EB] text-xs mt-1 block not-italic font-medium">
                              — Robin Fischer
                            </cite>
                          </blockquote>
                        )}
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Key numbers */}
      <section className="bg-white py-16 md:py-20">
        <div className="container">
          <AnimatedSection>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {[
                { number: "30+", label: "År av erfarenhet", sub: "Grundat 1993" },
                { number: "4", label: "Lasrar", sub: "CO2 och rörlaser" },
                { number: "0,1 mm", label: "Tolerans", sub: "Garanterad precision" },
                { number: "24h", label: "Leveranstid", sub: "Enkla projekt" },
              ].map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="font-heading text-3xl md:text-4xl font-bold text-[#2563EB]">{stat.number}</div>
                  <div className="text-gray-900 font-medium text-sm mt-1">{stat.label}</div>
                  <div className="text-gray-500 text-xs mt-0.5">{stat.sub}</div>
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Values */}
      <section className="bg-gray-50 py-20 md:py-28">
        <div className="container">
          <AnimatedSection>
            <div className="text-center mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Vad som driver oss</span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Våra värderingar
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              { title: "Snabbhet", desc: "Vi har byggt vår verksamhet kring att vara snabba. Leverans samma dag för enkla projekt, komplexa inom två dagar." },
              { title: "Kvalitet", desc: "Toleranser på en tiondel och fokus på passform. Vi löser problem i 99,9% av fallen och tar hand om resten direkt." },
              { title: "Problemlösning", desc: "Vi tar emot allt från CAD-ritningar till skisser på papper. Vi hjälper er hela vägen från idé till färdig produkt." },
              { title: "Pålitlighet", desc: "Stora kunder som Volvo förlitar sig på oss för alla sina prototyper. Det förtroendet har vi byggt genom att alltid leverera." },
              { title: "Expertis", desc: "30 års erfarenhet av metallbearbetning. Djup kunskap om material, skärparametrar och konstruktion." },
              { title: "Partnerskap", desc: "Vi ser oss inte som en leverantör utan som en partner. Vi ger råd, optimerar och ser till att ni får bästa möjliga resultat." },
            ].map((v, i) => (
              <AnimatedSection key={v.title} delay={i * 100}>
                <div className="bg-white border border-gray-200 rounded-xl p-6 h-full hover:border-[#2563EB]/20 hover:shadow-sm transition-all duration-300">
                  <h3 className="font-heading text-lg font-semibold text-gray-900">{v.title}</h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed">{v.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* Certifications */}
          <AnimatedSection delay={200}>
            <div className="mt-16 bg-white border border-gray-200 rounded-xl p-8 md:p-12 max-w-3xl mx-auto">
              <h3 className="font-heading text-xl font-semibold text-gray-900 text-center">Certifieringar</h3>
              <p className="text-gray-500 text-center mt-3 leading-relaxed">
                Vi genomgår certifiering enligt ISO 9001, ISO 14001 och ISO 45001. 
                Certifieringarna säkerställer dokumenterade processer, miljöansvar och 
                arbetsmiljöstandard, och möjliggör leveranser till försvarsindustrin 
                och andra krävande sektorer.
              </p>
              <p className="text-gray-500 text-center mt-2 text-sm leading-relaxed">
                "Dokumentation, strukturen företagsstruktur som gör på ett sådant sätt så att alla 
                arbetar på samma sätt. Det ska inte spela någon roll om vi har en viss person sjuk 
                utan de andra skulle kunna göra precis samma sak."
              </p>
              <div className="flex flex-wrap justify-center gap-4 mt-6">
                {["ISO 9001", "ISO 14001", "ISO 45001"].map((cert) => (
                  <div key={cert} className="px-4 py-2 bg-[#2563EB]/10 border border-[#2563EB]/20 rounded-lg">
                    <span className="text-[#2563EB] text-sm font-medium">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Team section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <LazyImage
                src={IMAGES.teamOldSite}
                alt="ASPAB-teamet i verkstaden"
                className="w-full rounded-xl"
              />
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">Vårt team</span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Framgång bygger på människorna bakom maskinerna
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Marknadens modernaste utrustning är bara halva ekvationen. Det är våra 
                  medarbetares kompetens, engagemang och problemlösningsförmåga som gör 
                  den verkliga skillnaden för våra kunder.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vårt team består av erfarna tekniker och operatorer som brinner för 
                  precision och kvalitet. Vi investerar kontinuerligt i kompetensutveckling 
                  för att säkerställa att vi alltid ligger i framkant.
                </p>
                <div className="mt-8 flex items-center gap-4">
                  <LazyImage
                    src={IMAGES.robinFischer}
                    alt="Robin Fischer, ASPAB"
                    className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                  />
                  <div>
                    <div className="text-gray-900 font-semibold">Robin Fischer</div>
                    <div className="text-gray-500 text-sm">Ägare & VD, ASPAB</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Kreditvärdighet badge + CTA */}
      <section className="bg-gray-50 py-16 border-t border-gray-100">
        <div className="container">
          <AnimatedSection>
            <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-12">
              <LazyImage
                src={IMAGES.kreditvardighet}
                alt="Högsta kreditvärdighet AAA"
                className="h-16 w-auto"
              />
              <p className="text-gray-500 text-sm text-center md:text-left">
                ASPAB har högsta kreditvärdighet (AAA), vilket visar på en stabil och 
                pålitlig affärspartner.
              </p>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={100}>
            <div className="text-center">
              <h3 className="font-heading text-2xl font-bold text-gray-900">
                Vill du veta mer om ASPAB?
              </h3>
              <p className="text-gray-500 mt-2">
                Kontakta oss för att diskutera hur vi kan hjälpa er med ert nästa projekt.
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-4">
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-lg hover:bg-[#3B82F6] transition-colors"
                >
                  Kontakta oss <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  href="/referenser"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-gray-700 font-semibold rounded-lg border border-gray-200 hover:border-[#2563EB]/30 hover:text-[#2563EB] transition-colors"
                >
                  Se våra referenser
                </Link>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Kunskapsbank */}
      <RelatedArticles
        heading="Läs mer om kvalitet och hållbarhet"
        subheading="Fördjupa dig i hur ASPAB arbetar med kvalitet, certifieringar och hållbar produktion."
        articles={[
          { title: "ISO-certifiering för plåtverkstäder", slug: "iso-certifiering-metallbearbetning", excerpt: "Vad ISO 9001, 14001 och 45001 innebär och varför det är viktigt för dina leverantörer." },
          { title: "Kvalitetskontroll inom metallbearbetning", slug: "forsta-bit-kontroll", excerpt: "Från första bit-kontroll till slutinspektion – så säkerställer vi kvalitet." },
          { title: "Hållbar metallbearbetning", slug: "hallbar-tillverkning-plat", excerpt: "Energieffektivisering, materialoptimering och återvinning i modern plåtindustri." },
          { title: "Välja rätt plåtleverantör", slug: "valja-underleverantor-plat", excerpt: "Checklista och tips för att hitta en pålitlig partner för laserskärning och bockning." },
          { title: "Svensk stålindustri 2025", slug: "stalsorter-guide", excerpt: "Trender, utmaningar och möjligheter för svensk stål- och metallbearbetningsindustri." },
          { title: "Prototyp till serie", slug: "laserskarning-prototyper", excerpt: "Så tar du en idé från prototyp till serieproduktion med rätt partner." },
        ]}
      />
    </Layout>
  );
}
